import { useId, useState, type FormEvent } from 'react';
import type { UserTaskAction } from '@cis/api-client';
import { Modal } from '../../ui/modal.tsx';
import { ACTION_INFO, isUserAction } from './action-labels.ts';
import type { TaskWriter } from './use-task-writer.ts';

const MAX_REASON = 500;

function ActionDialog(props: {
  readonly action: UserTaskAction;
  readonly writer: TaskWriter;
  readonly onClose: () => void;
}): React.JSX.Element {
  const info = ACTION_INFO[props.action];
  const fieldId = useId();
  const [text, setText] = useState('');
  const required = info.text === 'reason' || info.text === 'note';
  const empty = text.trim() === '';
  const onSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (required && empty) return;
    const outcome = await props.writer.send({ kind: 'action', action: props.action, text });
    if (outcome === 'ok' || outcome === 'conflict') props.onClose();
  };
  return (
    <Modal title={info.label} onClose={props.onClose} busy={props.writer.pending}>
      <form className="field" onSubmit={(e) => void onSubmit(e)}>
        <label className="field__label" htmlFor={fieldId}>
          {info.prompt}
        </label>
        <textarea
          id={fieldId}
          className="field__input"
          rows={4}
          maxLength={MAX_REASON}
          required={required}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <div className="modal__actions">
          <button
            type="button"
            className="btn btn--secondary"
            onClick={props.onClose}
            disabled={props.writer.pending}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn--primary"
            disabled={props.writer.pending || (required && empty)}
            aria-busy={props.writer.pending}
          >
            {props.writer.pending ? 'Saving…' : info.label}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** One button per action the API says this person may take now (qa Q21); nothing else renders. */
export function TaskActions(props: {
  readonly actions: readonly string[];
  readonly writer: TaskWriter;
}): React.JSX.Element | null {
  const [open, setOpen] = useState<UserTaskAction | null>(null);
  const actions = props.actions.filter(isUserAction);
  if (actions.length === 0) return null;
  const onClick = (action: UserTaskAction): void => {
    if (ACTION_INFO[action].text === null) {
      void props.writer.send({ kind: 'action', action });
      return;
    }
    setOpen(action);
  };
  return (
    <div className="form-row" role="group" aria-label="Task actions">
      {actions.map((action) => (
        <button
          key={action}
          type="button"
          className="btn btn--primary"
          disabled={props.writer.pending}
          aria-busy={props.writer.pending}
          onClick={() => onClick(action)}
        >
          {ACTION_INFO[action].label}
        </button>
      ))}
      {open !== null && (
        <ActionDialog action={open} writer={props.writer} onClose={() => setOpen(null)} />
      )}
    </div>
  );
}
